import { VariantUpdatedEvent, VariantSelectedEvent } from '@theme/events';

class VariantPicker extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Gère le changement de variante.
   * @param {Event} event
   */
  variantChanged(event) {
    const target = event.target;

    if (!(target instanceof HTMLElement)) return;

    // 1. Met à jour visuellement l'option sélectionnée
    this.updateSelectedOption(target);

    // 2. Informe le reste du thème qu'une option vient d'être sélectionnée
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: target.dataset.optionValueId ?? '',
      }),
    );

    // 3. Récupère auprès de Shopify la variante correspondant
    //    à la combinaison d'options sélectionnée
    this.fetchUpdatedSection(this.buildRequestUrl());

    // L'URL finale sera mise à jour APRÈS que Shopify nous aura
    // retourné la vraie variante sélectionnée.
  }

  /**
   * Met à jour l'option sélectionnée.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`,
      );

      if (!targetElement) {
        throw new Error('Target element not found');
      }

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;

      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue,
      );

      if (!newSelectedOption) {
        throw new Error('Option not found');
      }

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Construit l'URL permettant à Shopify de déterminer
   * la variante correspondant aux options choisies.
   */
  buildRequestUrl() {
    const productUrl = this.dataset.productUrl;

    const params = new URLSearchParams();

    params.set(
      'option_values',
      this.selectedOptionsValues.join(','),
    );

    return `${productUrl}?${params.toString()}`;
  }

  /**
   * Demande à Shopify les données actualisées de la variante.
   */
  fetchUpdatedSection(requestUrl) {
    this.#abortController?.abort();

    this.#abortController = new AbortController();

    fetch(requestUrl, {
      signal: this.#abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Erreur Shopify lors du chargement de la variante : ${response.status}`,
          );
        }

        return response.text();
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(
          responseText,
          'text/html',
        );

        const newVariantPicker = html.querySelector('variant-picker');

        if (!newVariantPicker) {
          throw new Error(
            'Aucun variant-picker trouvé dans la réponse Shopify',
          );
        }

        const jsonElement = newVariantPicker.querySelector(
          'script[type="application/json"]',
        );

        if (!jsonElement?.textContent) {
          throw new Error(
            'Aucune donnée JSON de variante trouvée',
          );
        }

        const variant = JSON.parse(jsonElement.textContent);

        if (!variant?.id) {
          throw new Error(
            'Aucun ID de variante valide retourné par Shopify',
          );
        }

        /*
         * IMPORTANT :
         * On remplace le contenu du picker par celui retourné
         * par Shopify. Le hidden input name="id" contient alors
         * l'ID de la nouvelle variante.
         */
      const variantInput = this.querySelector('input[name="id"]');

if (variantInput) {
  variantInput.value = String(variant.id);
}

        /*
         * Met à jour l'URL même si le produit est affiché
         * dans /pages/page-produit.
         */
        this.updateUrl(variant.id);

        /*
         * Informe product-form.js que la variante est prête.
         */
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdatedEvent(
              variant,
              this.selectedOptionId,
              {
                html,
                productId: this.dataset.productId ?? '',
              },
            ),
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }

        console.error(
          'Erreur lors de la mise à jour de la variante :',
          error,
        );
      });
  }

  /**
   * Met à jour l'URL avec le véritable ID de variante.
   *
   * Fonctionne également sur /pages/page-produit,
   * contrairement à l'ancien code limité aux templates "product".
   */
  updateUrl(variantId) {
    if (!variantId) return;

    // Ne pas modifier l'URL dans les cartes / Quick Add.
    if (
      this.closest('product-card') ||
      this.closest('quick-add-dialog')
    ) {
      return;
    }

    const url = new URL(window.location.href);

    url.searchParams.set(
      'variant',
      String(variantId),
    );

    if (url.href !== window.location.href) {
      history.replaceState(
        {},
        '',
        url.toString(),
      );
    }
  }

  /**
   * Option actuellement sélectionnée.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected], fieldset input:checked',
    );

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * ID de la valeur d'option actuellement sélectionnée.
   */
  get selectedOptionId() {
    const { selectedOption } = this;

    if (!selectedOption) return undefined;

    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error(
        "Aucun ID de valeur d'option trouvé",
      );
    }

    return optionValueId;
  }

  /**
   * IDs de toutes les valeurs d'options sélectionnées.
   */
  get selectedOptionsValues() {
    const selectedOptions = Array.from(
      this.querySelectorAll(
        'select option[selected], fieldset input:checked',
      ),
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) {
        throw new Error(
          "Aucun ID de valeur d'option trouvé",
        );
      }

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define(
    'variant-picker',
    VariantPicker,
  );
}